import { DateIntervalEnum } from "../../enums/DateInterval";
const SUPPORTED_DATE_INTERVALS = [
    DateIntervalEnum.YEAR,
    DateIntervalEnum.MONTH,
    DateIntervalEnum.WEEK,
    DateIntervalEnum.DAY
];
export const DATE_INTERVAL_FILTER_TYPES = SUPPORTED_DATE_INTERVALS.map((interval) => ({
    value: interval,
    label: interval.charAt(0).toUpperCase() + interval.slice(1)
}));
export const TIME_WAITING_FILTER_TYPES = [
    { value: "between", label: "Is Between" },
    { value: "longer_than", label: "Longer Than" },
    { value: "shorter_than", label: "Shorter Than" }
];
export const AGE_YEARS_FILTER_TYPES = [
    { value: "between", label: "Is Between" },
    { value: "older_than", label: "Older Than" },
    { value: "younger_than", label: "Younger Than" }
];
export const MATCH_TYPES = [
    { value: "ALL", label: "All" },
    { value: "ANY", label: "Any" },
    { value: "NONE", label: "None" }
];
export const BOOLEAN_FILTER_TYPES = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" }
];
// Helper function to get default values for each input type
export const getDefaultValuesForInputType = (inputType) => {
    switch (inputType) {
        case "String":
            return "";
        case "Boolean":
            return true;
        case "NeedsFollowUpInput":
            return {
                value: true,
                amount: 7,
                interval: DateIntervalEnum.DAY
            };
        case "PrioritySearchUuidWhereFilterObjectInput":
            return {
                match: MATCH_TYPES[0].value,
                ids: []
            };
        case "PrioritySearchWhereFilterObjectInput":
        case "TagsInput":
            return {
                match: MATCH_TYPES[0].value,
                names: []
            };
        case "PriorityLocationInput":
            return {
                mode: "zip",
                distance_in_miles: null,
                postal_codes: [],
                state_ids: []
            };
        case "AgeYearsInput":
            return {
                filterType: AGE_YEARS_FILTER_TYPES[0].value,
                between_min: null,
                between_max: null,
                older_than: null,
                younger_than: null
            };
        case "TimeWaitingDateInput":
            return {
                filterType: TIME_WAITING_FILTER_TYPES[0].value,
                between_start: null,
                between_end: null,
                longer_than: null,
                shorter_than: null,
                between_interval: null,
                longer_than_interval: null,
                shorter_than_interval: null
            };
        default:
            return { value: "" };
    }
};
export const buildAvailablePrioritySearchFilters = () => {
    // Define all possible PrioritySearchWhereFilter fields with their associated input types
    const allPossibleFields = [
        { id: "age_years", name: "Patient Age", inputType: "AgeYearsInput" },
        {
            id: "needs_follow_up",
            name: "Needs Follow-up",
            inputType: "NeedsFollowUpInput"
        },
        {
            id: "without_assigned_provider",
            name: "With Assigned Providers",
            inputType: "Boolean"
        },
        {
            id: "days_available",
            name: "Days Available",
            inputType: "PrioritySearchWhereFilterObjectInput"
        },
        {
            id: "times_available",
            name: "Time Available",
            inputType: "PrioritySearchWhereFilterObjectInput"
        },
        {
            id: "location",
            name: "Location",
            inputType: "PriorityLocationInput"
        },
        {
            id: "priority_status_id",
            name: "Stage",
            inputType: "PrioritySearchUuidWhereFilterObjectInput"
        },
        { id: "search", name: "Search", inputType: "String" },
        {
            id: "tags",
            name: "Tags",
            inputType: "TagsInput"
        },
        {
            id: "time_waiting_date",
            name: "Time Waiting",
            inputType: "TimeWaitingDateInput"
        },
        {
            id: "insurance_ids",
            name: "Insurance",
            inputType: "PrioritySearchUuidWhereFilterObjectInput"
        },
        {
            id: "condition_ids",
            name: "Diagnoses",
            inputType: "PrioritySearchUuidWhereFilterObjectInput"
        },
        {
            id: "therapy_category_ids",
            name: "Modalities",
            inputType: "PrioritySearchUuidWhereFilterObjectInput"
        },
        {
            id: "therapy_sub_category_ids",
            name: "Skills",
            inputType: "PrioritySearchUuidWhereFilterObjectInput"
        }
    ];
    return allPossibleFields.map((field) => ({
        id: field.id,
        name: field.name,
        enabled: false,
        inputType: field.inputType
    }));
};
/**
 * Converts form values to a PriorityViewCreate payload
 * @param formValues - The form values from EditTableViewModal
 * @returns PriorityViewCreatePayload that matches the GraphQL type
 */
export const convertFormToPriorityViewCreate = (formValues) => {
    const payload = {
        name: formValues.name || undefined
    };
    // Build the filter object
    const whereFilter = {};
    // Process each enabled filter in filtering order
    Object.keys(formValues.fields).forEach((filterId) => {
        const fieldConfig = formValues.fields[filterId];
        if (fieldConfig === undefined || fieldConfig === null)
            return;
        switch (filterId) {
            case "age_years": {
                const { filterType, between_min, between_max, older_than, younger_than } = fieldConfig;
                if (filterType === "between" && between_min && between_max) {
                    whereFilter.age_years = {
                        between: [parseInt(between_min), parseInt(between_max)]
                    };
                }
                else if (filterType === "older_than" && older_than) {
                    whereFilter.age_years = {
                        older_than: parseInt(older_than)
                    };
                }
                else if (filterType === "younger_than" && younger_than) {
                    whereFilter.age_years = {
                        younger_than: parseInt(younger_than)
                    };
                }
                break;
            }
            case "days_available": {
                const { match, names } = fieldConfig;
                if (names && names.length > 0) {
                    whereFilter[filterId] = {
                        match: match,
                        names: names
                    };
                }
                break;
            }
            case "times_available": {
                const { match, names } = fieldConfig;
                if (names && names.length > 0) {
                    whereFilter[filterId] = {
                        match: match,
                        names: names
                    };
                }
            }
            case "tags": {
                const { match, names } = fieldConfig;
                if (names && names.length > 0) {
                    whereFilter[filterId] = {
                        match: match,
                        names: names
                    };
                }
                break;
            }
            case "location": {
                const { mode, distance_in_miles, postal_codes, state_ids } = fieldConfig;
                if (mode === "states") {
                    if (state_ids && state_ids.length > 0) {
                        whereFilter.location = {
                            state_ids: state_ids
                                .filter(Boolean)
                                .map((id) => String(id).trim().toUpperCase())
                                .filter(Boolean)
                        };
                    }
                    break;
                }
                // Default: zip mode
                if (distance_in_miles &&
                    postal_codes &&
                    postal_codes.length > 0) {
                    whereFilter.location = {
                        distance_in_miles: distance_in_miles
                            ? parseFloat(String(distance_in_miles))
                            : undefined,
                        postal_codes: postal_codes
                    };
                }
                break;
            }
            case "priority_status_id": {
                const { match, ids } = fieldConfig;
                if (ids && ids.length > 0) {
                    whereFilter.priority_status_id = {
                        match: match,
                        // "Not Set" is a special value/status that doesn't exist in the db, but needs to be able to be passed in.
                        // When it's passed in, it can be used to find entries that have no status set.
                        ids: ids.map((id) => id !== "NOT_SET" ? id : null)
                    };
                }
                break;
            }
            case "search": {
                const value = fieldConfig;
                if (value && value.trim()) {
                    whereFilter[filterId] = value.trim();
                }
                break;
            }
            case "needs_follow_up": {
                const { value, amount, interval } = fieldConfig;
                if (typeof value === "boolean" && amount && interval) {
                    whereFilter[filterId] = {
                        value,
                        amount: parseInt(String(amount)),
                        interval
                    };
                }
                break;
            }
            case "without_assigned_provider": {
                if (typeof fieldConfig === "boolean") {
                    whereFilter[filterId] = !fieldConfig;
                }
                break;
            }
            case "time_waiting_date": {
                const { filterType, between_start, between_end, longer_than, shorter_than, between_interval, longer_than_interval, shorter_than_interval } = fieldConfig;
                if (filterType === "between" && between_start && between_end) {
                    const startString = `${between_start} ${between_interval}`;
                    const endString = `${between_end} ${between_interval}`;
                    whereFilter.time_waiting_date = {
                        between: [startString, endString]
                    };
                }
                else if (filterType === "longer_than" && longer_than) {
                    const longerThanString = `${longer_than} ${longer_than_interval}`;
                    whereFilter.time_waiting_date = {
                        longer_than: longerThanString
                    };
                }
                else if (filterType === "shorter_than" && shorter_than) {
                    const shorterThanString = `${shorter_than} ${shorter_than_interval}`;
                    whereFilter.time_waiting_date = {
                        shorter_than: shorterThanString
                    };
                }
                break;
            }
            case "insurance_ids":
            case "condition_ids":
            case "therapy_category_ids":
            case "therapy_sub_category_ids": {
                const { match, ids } = fieldConfig;
                if (ids && ids.length > 0) {
                    whereFilter[filterId] = {
                        match: match,
                        ids: ids
                    };
                }
                break;
            }
        }
    });
    // Only add where filter if it has properties
    if (Object.keys(whereFilter).length > 0) {
        payload.filter = {
            where: whereFilter
        };
    }
    // Process sorting order
    if (formValues.sort && payload.filter) {
        payload.filter.sort = formValues.sort;
    }
    return payload;
};
const splitDateInterval = (dateInterval) => {
    const [value, interval] = dateInterval.split(" ");
    return { value: value || "", interval: interval };
};
/**
 * Converts a PriorityView to EditTableViewFormValues for form initialization
 * @param priorityView - The PriorityView object from the API
 * @returns EditTableViewFormValues that can be used to initialize the form
 */
export const convertPriorityViewToFormValues = (priorityView) => {
    const formValues = {
        name: priorityView.name || "",
        fields: {}
    };
    const whereFilter = priorityView.where;
    if (!whereFilter) {
        return formValues;
    }
    // Process each filter field and add to filteringOrder and fields
    const filterFields = Object.keys(whereFilter);
    filterFields.forEach((fieldId) => {
        const fieldValue = whereFilter[fieldId];
        if (fieldValue === undefined || fieldValue === null)
            return;
        switch (fieldId) {
            case "age_years": {
                const ageYears = fieldValue;
                if (ageYears.between && ageYears.between.length === 2) {
                    formValues.fields[fieldId] = {
                        filterType: "between",
                        between_min: ageYears.between[0],
                        between_max: ageYears.between[1],
                        older_than: null,
                        younger_than: null
                    };
                }
                else if (ageYears.older_than) {
                    formValues.fields[fieldId] = {
                        filterType: "older_than",
                        between_min: null,
                        between_max: null,
                        older_than: ageYears.older_than,
                        younger_than: null
                    };
                }
                else if (ageYears.younger_than) {
                    formValues.fields[fieldId] = {
                        filterType: "younger_than",
                        between_min: null,
                        between_max: null,
                        older_than: null,
                        younger_than: ageYears.younger_than
                    };
                }
                break;
            }
            case "days_available":
            case "times_available":
            case "tags": {
                formValues.fields[fieldId] = {
                    match: fieldValue.match || MATCH_TYPES[0].value,
                    names: fieldValue.names?.filter(Boolean) || []
                };
                break;
            }
            case "location": {
                formValues.fields[fieldId] = {
                    mode: fieldValue.state_ids &&
                        fieldValue.state_ids.filter(Boolean).length > 0
                        ? "states"
                        : "zip",
                    distance_in_miles: fieldValue.distance_in_miles,
                    postal_codes: fieldValue.postal_codes?.filter(Boolean) || [],
                    state_ids: fieldValue.state_ids
                        ?.filter(Boolean)
                        .map((id) => String(id).trim().toUpperCase())
                        .filter(Boolean) || []
                };
                break;
            }
            case "priority_status_id":
            case "insurance_ids":
            case "condition_ids":
            case "therapy_category_ids":
            case "therapy_sub_category_ids": {
                formValues.fields[fieldId] = {
                    match: fieldValue.match || MATCH_TYPES[0].value,
                    // "Not Set" is a special value/status that doesn't exist in the db, but needs to be able to be passed in.
                    // When it's passed in, it can be used to find entries that have no status set.
                    // NOTE: Mantine `MultiSelect` expects `value` to be `string[]`.
                    // The backend represents "Not Set" as `null`, but the UI represents it as "NOT_SET".
                    ids: fieldValue.ids
                        ?.map((id) => {
                        if (fieldId === "priority_status_id") {
                            // Normalize both persisted `null` and any legacy "NOT_SET" to the UI sentinel.
                            return id == null || id === "NOT_SET"
                                ? "NOT_SET"
                                : id;
                        }
                        return id;
                    })
                        .filter((id) => typeof id === "string") || []
                };
                break;
            }
            case "search": {
                const value = fieldValue;
                formValues.fields[fieldId] = value || "";
                break;
            }
            case "needs_follow_up": {
                if (typeof fieldValue === "boolean") {
                    formValues.fields[fieldId] = {
                        value: fieldValue,
                        amount: 7,
                        interval: DateIntervalEnum.DAY
                    };
                    break;
                }
                formValues.fields[fieldId] = {
                    value: fieldValue.value ?? true,
                    amount: fieldValue.amount ?? 7,
                    interval: fieldValue.interval ?? DateIntervalEnum.DAY
                };
                break;
            }
            case "without_assigned_provider": {
                formValues.fields[fieldId] = !Boolean(fieldValue);
                break;
            }
            case "time_waiting_date": {
                const timeWaiting = fieldValue;
                if (timeWaiting.between && timeWaiting.between.length === 2) {
                    const [startString, endString] = timeWaiting.between;
                    const { value: between_start, interval: between_interval } = splitDateInterval(startString || "");
                    const { value: between_end } = splitDateInterval(endString || "");
                    formValues.fields[fieldId] = {
                        filterType: "between",
                        between_start: between_start,
                        between_end: between_end,
                        between_interval: between_interval,
                        longer_than: null,
                        shorter_than: null
                    };
                }
                else if (timeWaiting.longer_than) {
                    const { value: longer_than, interval: longer_than_interval } = splitDateInterval(timeWaiting.longer_than || "");
                    formValues.fields[fieldId] = {
                        filterType: "longer_than",
                        between_start: null,
                        between_end: null,
                        between_interval: null,
                        longer_than: longer_than,
                        longer_than_interval: longer_than_interval,
                        shorter_than: null,
                        shorter_than_interval: null
                    };
                }
                else if (timeWaiting.shorter_than) {
                    const { value: shorter_than, interval: shorter_than_interval } = splitDateInterval(timeWaiting.shorter_than || "");
                    formValues.fields[fieldId] = {
                        filterType: "shorter_than",
                        between_start: null,
                        between_end: null,
                        between_interval: null,
                        longer_than: null,
                        longer_than_interval: null,
                        shorter_than: shorter_than,
                        shorter_than_interval: shorter_than_interval
                    };
                }
                break;
            }
        }
    });
    return formValues;
};
